import React, { useMemo } from 'react';
import { IItemForGetShortInfo } from 'types';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import { ISelectedCategorySubcategory } from '../index';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import FormSelect from 'components/Select/FormSelect';
import { SubcategoriesMultipleSelect } from './SubcategoriesMultipleSelect';
import style from './index.module.scss';

interface IProps {
	setSelectedCategoriesSubcategories: (value: ISelectedCategorySubcategory[]) => void;
	fetchedCategories: IItemForGetShortInfo[];
	selectedCategoriesSubcategories: ISelectedCategorySubcategory[];
	selectedCategorySubcategory: ISelectedCategorySubcategory;
	IDSelectedRestaurant: string;
	isLoading: boolean;
}

export const CategorySubcategoryBlock: React.FC<IProps> = React.memo(
	({
		setSelectedCategoriesSubcategories,
		fetchedCategories,
		selectedCategoriesSubcategories,
		selectedCategorySubcategory,
		IDSelectedRestaurant,
		isLoading,
	}) => {
		const { t } = useTranslation('error');
		const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectCategory') }], [t]);

		const categories = fetchedCategories.map((el) => {
			const reservedCategories = selectedCategoriesSubcategories
				.filter(
					(categoriesSubcategories) =>
						categoriesSubcategories.selectedCategoryId !== selectedCategorySubcategory.selectedCategoryId,
				)
				.map((categorySubcategory) => categorySubcategory.selectedCategoryId);

			return {
				id: el.id,
				name: el.name,
				disabled: reservedCategories.includes(el.id),
			};
		});

		const handleRemoveBlock = () => {
			const updatedSelectedCategoriesSubcategories = selectedCategoriesSubcategories
				.filter((categorySubcategory) => categorySubcategory.index !== selectedCategorySubcategory.index)
				.map((categorySubcategory, index) => ({ ...categorySubcategory, index }));
			setSelectedCategoriesSubcategories(updatedSelectedCategoriesSubcategories);
		};

		const handleChangeCategory = (value: Nullable<string>) => {
			const updatedSelectedCategoriesSubcategories = selectedCategoriesSubcategories.map((categorySubcategory) =>
				categorySubcategory.index === selectedCategorySubcategory.index
					? {
							index: categorySubcategory.index,
							selectedCategoryId: value ? value : null,
							uniqueId: categorySubcategory.uniqueId,
							selectedSubcategoryIds: [],
					  }
					: categorySubcategory,
			);
			setSelectedCategoriesSubcategories(updatedSelectedCategoriesSubcategories);
		};

		const handleChangeSubcategory = (value: string[]) => {
			const updatedSelectedCategoriesSubcategories = selectedCategoriesSubcategories.map((categorySubcategory) =>
				categorySubcategory.index === selectedCategorySubcategory.index
					? {
							index: categorySubcategory.index,
							selectedCategoryId: categorySubcategory.selectedCategoryId,
							uniqueId: categorySubcategory.uniqueId,
							selectedSubcategoryIds: value,
					  }
					: categorySubcategory,
			);
			setSelectedCategoriesSubcategories(updatedSelectedCategoriesSubcategories);
		};

		return (
			<div className={style.container}>
				<div className={style.deleteBtnWrapper}>
					<div className={style.deleteBtn} onClick={handleRemoveBlock}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#bin`} />
						</svg>
					</div>
				</div>
				<FormSelect
					name={`IDSelectedCategory-${selectedCategorySubcategory.index}`}
					label="categoryLabel"
					options={categories}
					loading={isLoading}
					validationRules={validationRules}
					onChange={handleChangeCategory}
				/>
				<SubcategoriesMultipleSelect
					setIDsSubcategory={handleChangeSubcategory}
					index={selectedCategorySubcategory.index}
					IDSelectedCategory={selectedCategorySubcategory.selectedCategoryId}
					IDsSelectedSubcategories={selectedCategorySubcategory.selectedSubcategoryIds}
					IDSelectedRestaurant={IDSelectedRestaurant}
				/>
			</div>
		);
	},
);
